import { createSlice } from '@reduxjs/toolkit';
import { difference, union } from 'es-toolkit';

import { LOAD_UPCOMING_CATALOGS_SUCCESS, LoadUpcomingCatalogsSuccessAction } from '@/redux/modules/actions';
import { loadCatalogs } from '@/redux/modules/catalog/catalogs/catalog.actions';
import { fetchHomeData } from '@/redux/modules/home/home.actions';
import { fetchAllSellers, fetchSellersByIds } from '@/redux/modules/seller/seller.actions';
import { defaultSellerSlice } from '@/redux/modules/seller/seller.types';
import { safelyEscapeText } from '@/utils/sanitize';

const sellerSlice = createSlice({
    extraReducers: (builder) => {
        builder.addCase(fetchAllSellers.pending, (slice) => {
            slice.loadedSellers = false;
            slice.loadingSellers = true;
        });
        builder.addCase(fetchAllSellers.fulfilled, (slice, { payload }) => {
            payload?.forEach((seller) => {
                slice.byId[seller.sellerId] = {
                    ...slice.byId[seller.sellerId],
                    ...seller,
                    name: safelyEscapeText(seller.name),
                };
                slice.loaded[seller.sellerId] = Date.now();
                slice.loading = difference(slice.loading, [seller.sellerId]);
            });
            slice.loadedSellers = true;
            slice.loadingSellers = false;
        });
        builder.addCase(fetchAllSellers.rejected, (slice) => {
            slice.loadingSellers = false;
        });

        builder.addCase(fetchSellersByIds.pending, (slice, { meta }) => {
            slice.loading = union(slice.loading, meta.arg);
        });
        builder.addCase(fetchSellersByIds.fulfilled, (slice, { payload }) => {
            payload.sellers?.forEach((seller) => {
                slice.byId[seller.sellerId] = {
                    ...slice.byId[seller.sellerId],
                    ...seller,
                    name: safelyEscapeText(seller.name),
                };
                slice.loaded[seller.sellerId] = Date.now();
                slice.loading = difference(slice.loading, [seller.sellerId]);
            });
        });
        builder.addCase(fetchSellersByIds.rejected, (slice, { meta }) => {
            slice.loading = difference(slice.loading, meta.arg);
        });

        builder.addCase(loadCatalogs.fulfilled, (slice, { payload }) => {
            payload.data.sellers?.forEach((seller) => {
                slice.byId[seller.sellerId] = {
                    ...slice.byId[seller.sellerId],
                    ...seller,
                    name: safelyEscapeText(seller.name),
                };
                slice.loaded[seller.sellerId] = Date.now();
                slice.loading = difference(slice.loading, [seller.sellerId]);
            });
        });

        builder.addCase(fetchHomeData.fulfilled, (slice, { payload }) => {
            payload.sellers?.forEach((seller) => {
                slice.byId[seller.sellerId] = {
                    ...slice.byId[seller.sellerId],
                    ...seller,
                    name: safelyEscapeText(seller.name),
                };
                slice.loaded[seller.sellerId] = Date.now();
                slice.loading = difference(slice.loading, [seller.sellerId]);
            });
        });

        builder.addCase<typeof LOAD_UPCOMING_CATALOGS_SUCCESS, LoadUpcomingCatalogsSuccessAction>(
            LOAD_UPCOMING_CATALOGS_SUCCESS,
            (slice, { payload }) => {
                payload.sellers?.forEach((seller) => {
                    slice.byId[seller.sellerId] = {
                        ...slice.byId[seller.sellerId],
                        ...seller,
                        name: safelyEscapeText(seller.name),
                    };
                    slice.loaded[seller.sellerId] = Date.now();
                    slice.loading = difference(slice.loading, [seller.sellerId]);
                });
            }
        );
    },
    initialState: defaultSellerSlice,
    name: 'seller',
    reducers: {},
});

export const { reducer: sellerReducer } = sellerSlice;
